import { useDispatch } from "react-redux";

import { getAdminAreaByIdApi } from "@/api/adminArea/adminAreaApi";
import { useFetchAdminAreasByIds } from "@/api/adminArea/userAdminAreaAPI";
import { getValueOfHighestLevelAdminArea } from "@/helpers/datasetAttributeHelper";
import {
  setAdminAreaSettings,
  setAdminAreaSettingsData,
  setSelectedAreaIds
} from "@/store/slice/globalFilterSlice";
import type { AdministrativeArea } from "@/types/adminArea";
import type { AdminAreaSettings } from "@/types/spatial";

import { useMap } from "../map/core/useMapContext";

export const useGlobalAdminAreaSync = () => {
  const dispatch = useDispatch();
  const { fitMapToLayerBound } = useMap();
  const fetchAdminAreasByIds = useFetchAdminAreasByIds();

  const syncGlobalMultipleArea = async (ids: string[]) => {
    if (ids.length === 0) return;
    try {
      const results = await fetchAdminAreasByIds(ids);
      const allData = results.filter(
        (result): result is AdministrativeArea =>
          result !== null && result !== undefined
      );
      if (allData.length === 0) return;
      dispatch(setAdminAreaSettingsData({ data: allData }));
      dispatch(setSelectedAreaIds(ids));
      fitMapToLayerBound({ data: allData });
    } catch {
      return;
    }
  };

  const syncGlobalSingleArea = async (adminArea: AdminAreaSettings) => {
    const adminAreaId = getValueOfHighestLevelAdminArea(adminArea);
    if (!adminAreaId) return;
    try {
      const adminAreaDetail = await getAdminAreaByIdApi(adminAreaId);
      dispatch(setAdminAreaSettingsData({ data: [adminAreaDetail] }));
      dispatch(setSelectedAreaIds([adminAreaId]));
      fitMapToLayerBound({ data: [adminAreaDetail] });
    } catch {
      return;
    }
  };

  const syncAdminAreaToGlobalFilter = async ({
    adminArea,
    adminAreaIds,
    isAdminTreeEnabled
  }: {
    adminArea: AdminAreaSettings | null;
    adminAreaIds: string[];
    isAdminTreeEnabled: boolean;
  }) => {
    dispatch(setAdminAreaSettings(adminArea));
    if (isAdminTreeEnabled) {
      await syncGlobalMultipleArea(adminAreaIds);
    } else if (adminArea) {
      await syncGlobalSingleArea(adminArea);
    }
  };

  return { syncAdminAreaToGlobalFilter };
};
